import React, { useEffect, useState, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { fetchRewardManagementData } from "../../../../../utils/axios";
import { RefreshCw, Download, ChevronDown } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const RewardManagementChart = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartType, setChartType] = useState("bar");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [downloadDropdownOpen, setDownloadDropdownOpen] = useState(false);
  const chartRef = useRef(null);

  const chartTypes = [
    { id: "bar", name: "Top Researchers (Bar)" },
    { id: "line", name: "Rewards Trend (Line)" },
    { id: "radar", name: "Priority Distribution (Radar)" },
    { id: "pie", name: "Severity Distribution (Pie)" },
    { id: "donut", name: "Status Distribution (Donut)" },
    { id: "heatmap", name: "Program vs Severity (Heatmap)" },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Use actual API in production, mock data for development
      let data;
      try {
        data = await fetchRewardManagementData();
      } catch (err) {
        console.log("Using mock data instead");
        data = getMockData();
      }
      setChartData(data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch data");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Bar Chart - Top 10 researchers by total rewards
  const getBarChartOption = () => {
    const researcherRewards = {};

    chartData.forEach((item) => {
      if (!researcherRewards[item.researcher_name]) {
        researcherRewards[item.researcher_name] = 0;
      }
      researcherRewards[item.researcher_name] += item.rewards_amount;
    });

    const sortedResearchers = Object.entries(researcherRewards)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    const researchers = sortedResearchers.map((item) => item[0]);
    const amounts = sortedResearchers.map((item) => item[1]);

    return {
      title: {
        text: "Top 10 Researchers by Rewards",
        left: "center",
      },
      tooltip: {
        trigger: "axis",
        formatter: "{b}: ${c}",
        axisPointer: {
          type: "shadow",
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        name: "Amount (USD)",
        axisLabel: {
          formatter: "${value}",
        },
      },
      yAxis: {
        type: "category",
        data: researchers,
        axisLabel: {
          interval: 0,
          width: 100,
          overflow: "truncate",
        },
      },
      series: [
        {
          name: "Total Rewards",
          type: "bar",
          data: amounts,
          itemStyle: {
            color: function (params) {
              // Generate colors based on index
              const colorList = [
                "#3b82f6",
                "#4f46e5",
                "#7c3aed",
                "#8b5cf6",
                "#6366f1",
                "#60a5fa",
                "#818cf8",
                "#a78bfa",
                "#c4b5fd",
                "#93c5fd",
              ];
              return colorList[params.dataIndex % colorList.length];
            },
          },
          label: {
            show: true,
            position: "right",
            formatter: "${c}",
          },
        },
      ],
    };
  };

  // Line Chart - Rewards over time
  const getLineChartOption = () => {
    const timeSeriesData = {};

    // Sort data by date first
    const sortedData = [...chartData].sort(
      (a, b) => new Date(a.updated_at) - new Date(b.updated_at)
    );

    sortedData.forEach((item) => {
      const date = new Date(item.updated_at);
      const formattedDate = date.toISOString().split("T")[0]; // YYYY-MM-DD format

      if (!timeSeriesData[formattedDate]) {
        timeSeriesData[formattedDate] = 0;
      }
      timeSeriesData[formattedDate] += item.rewards_amount;
    });

    const sortedDates = Object.keys(timeSeriesData).sort();
    const amounts = sortedDates.map((date) => timeSeriesData[date]);

    // Calculate cumulative amounts
    const cumulativeAmounts = [];
    let runningTotal = 0;
    amounts.forEach((amount) => {
      runningTotal += amount;
      cumulativeAmounts.push(runningTotal);
    });

    return {
      title: {
        text: "Rewards Trend Over Time",
        left: "center",
      },
      tooltip: {
        trigger: "axis",
        formatter: function (params) {
          const dateStr = params[0].name;
          const dailyValue = params[0].value;
          const cumulativeValue = params[1]?.value || 0;

          return `
            <div><b>${dateStr}</b></div>
            <div>${params[0].seriesName}: $${dailyValue.toFixed(2)}</div>
            <div>${
              params[1]?.seriesName || "Total"
            }: $${cumulativeValue.toFixed(2)}</div>
          `;
        },
      },
      legend: {
        data: ["Daily Rewards", "Cumulative Rewards"],
        bottom: 0,
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "60px",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: sortedDates,
        boundaryGap: false,
        axisLabel: {
          rotate: 45,
          formatter: function (value) {
            const date = new Date(value);
            return date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
          },
        },
      },
      yAxis: {
        type: "value",
        name: "Amount (USD)",
        axisLabel: {
          formatter: "${value}",
        },
      },
      series: [
        {
          name: "Daily Rewards",
          type: "line",
          data: amounts,
          smooth: true,
          symbol: "emptyCircle",
          symbolSize: 8,
          lineStyle: {
            width: 3,
            color: "#10b981",
          },
          itemStyle: {
            color: "#10b981",
          },
        },
        {
          name: "Cumulative Rewards",
          type: "line",
          data: cumulativeAmounts,
          smooth: true,
          symbol: "circle",
          symbolSize: 8,
          lineStyle: {
            width: 3,
            color: "#3b82f6",
          },
          itemStyle: {
            color: "#3b82f6",
          },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: "rgba(59, 130, 246, 0.5)",
                },
                {
                  offset: 1,
                  color: "rgba(59, 130, 246, 0.05)",
                },
              ],
            },
          },
        },
      ],
    };
  };

  // Radar Chart - Priority distribution
  const getRadarChartOption = () => {
    const priorityCounts = {};

    chartData.forEach((item) => {
      if (!priorityCounts[item.priority]) {
        priorityCounts[item.priority] = 0;
      }
      priorityCounts[item.priority]++;
    });

    const priorities = Object.keys(priorityCounts);
    const counts = priorities.map((priority) => priorityCounts[priority]);

    return {
      title: {
        text: "Priority Distribution",
        left: "center",
      },
      tooltip: {
        trigger: "item",
      },
      radar: {
        indicator: priorities.map((priority) => ({
          name: priority,
          max: Math.max(...counts) + 2,
        })),
        radius: "65%",
        splitNumber: 4,
        axisName: {
          color: "#333",
          backgroundColor: "#eee",
          borderRadius: 3,
          padding: [3, 5],
        },
      },
      series: [
        {
          name: "Priority Distribution",
          type: "radar",
          data: [
            {
              value: counts,
              name: "Submission Count",
              areaStyle: {
                color: "rgba(124, 58, 237, 0.6)",
              },
              lineStyle: {
                color: "#7c3aed",
              },
              itemStyle: {
                color: "#7c3aed",
              },
            },
          ],
        },
      ],
    };
  };

  // Pie Chart - Severity distribution
  const getPieChartOption = () => {
    const severityCounts = {};

    chartData.forEach((item) => {
      if (!severityCounts[item.severity]) {
        severityCounts[item.severity] = 0;
      }
      severityCounts[item.severity]++;
    });

    const pieData = Object.entries(severityCounts).map(([name, value]) => ({
      name,
      value,
    }));

    const severityColors = {
      Critical: "#ef4444",
      High: "#f97316",
      Moderate: "#f59e0b",
      Low: "#10b981",
    };

    return {
      title: {
        text: "Submissions by Severity",
        left: "center",
      },
      tooltip: {
        trigger: "item",
        formatter: "{b}: {c} ({d}%)",
      },
      legend: {
        orient: "vertical",
        right: 10,
        top: "center",
        data: Object.keys(severityCounts),
      },
      series: [
        {
          name: "Severity Distribution",
          type: "pie",
          radius: "70%",
          center: ["40%", "50%"],
          data: pieData,
          itemStyle: {
            color: function (params) {
              return severityColors[params.name] || "#8b5cf6";
            },
            borderRadius: 10,
            borderColor: "#fff",
            borderWidth: 2,
          },
          label: {
            formatter: "{b}: {c} ({d}%)",
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    };
  };

  // Donut Chart - Note status distribution
  const getDonutChartOption = () => {
    const statusCounts = {};

    chartData.forEach((item) => {
      if (!statusCounts[item.note_status]) {
        statusCounts[item.note_status] = 0;
      }
      statusCounts[item.note_status]++;
    });

    const donutData = Object.entries(statusCounts).map(([name, value]) => ({
      name,
      value,
    }));

    const statusColors = {
      approved: "#10b981",
      pending: "#f59e0b",
      submitted: "#3b82f6",
      rejected: "#ef4444",
    };

    const total = donutData.reduce((sum, item) => sum + item.value, 0);

    return {
      title: {
        text: "Note Status Distribution",
        left: "center",
      },
      tooltip: {
        trigger: "item",
        formatter: "{b}: {c} ({d}%)",
      },
      legend: {
        bottom: "0%",
        left: "center",
        data: Object.keys(statusCounts),
      },
      series: [
        {
          name: "Note Status",
          type: "pie",
          radius: ["40%", "70%"],
          center: ["50%", "50%"],
          avoidLabelOverlap: false,
          itemStyle: {
            color: function (params) {
              return statusColors[params.name] || "#8b5cf6";
            },
            borderRadius: 6,
            borderColor: "#fff",
            borderWidth: 2,
          },
          label: {
            show: false,
            position: "center",
          },
          emphasis: {
            label: {
              show: true,
              fontSize: "18",
              fontWeight: "bold",
            },
          },
          labelLine: {
            show: false,
          },
          data: donutData,
        },
      ],
      graphic: {
        elements: [
          {
            type: "text",
            left: "center",
            top: "45%",
            style: {
              text: total.toString(),
              fontSize: 28,
              fontWeight: "bold",
              textAlign: "center",
              fill: "#333",
            },
          },
          {
            type: "text",
            left: "center",
            top: "55%",
            style: {
              text: "Total",
              fontSize: 14,
              textAlign: "center",
              fill: "#999",
            },
          },
        ],
      },
    };
  };

  // Heatmap Chart - Program vs Severity
  const getHeatmapChartOption = () => {
    const programSeverityCounts = {};
    const programs = new Set();
    const severities = new Set();

    chartData.forEach((item) => {
      programs.add(item.program_title);
      severities.add(item.severity);

      const key = `${item.program_title}|${item.severity}`;
      if (!programSeverityCounts[key]) {
        programSeverityCounts[key] = 0;
      }
      programSeverityCounts[key]++;
    });

    const programsArray = Array.from(programs);
    const severitiesArray = Array.from(severities);

    const heatmapData = [];
    programsArray.forEach((program, i) => {
      severitiesArray.forEach((severity, j) => {
        const key = `${program}|${severity}`;
        const value = programSeverityCounts[key] || 0;
        heatmapData.push([i, j, value]);
      });
    });

    const severityOrder = {
      Critical: 0,
      High: 1,
      Moderate: 2,
      Low: 3,
    };

    const sortedSeverities = [...severitiesArray].sort((a, b) => {
      return severityOrder[a] !== undefined && severityOrder[b] !== undefined
        ? severityOrder[a] - severityOrder[b]
        : a.localeCompare(b);
    });

    return {
      title: {
        text: "Program vs Severity Heatmap",
        left: "center",
      },
      tooltip: {
        position: "top",
        formatter: function (params) {
          return `${programsArray[params.data[0]]} - ${
            sortedSeverities[params.data[1]]
          }: ${params.data[2]} submissions`;
        },
      },
      grid: {
        height: "50%",
        top: "15%",
        left: "3%",
        right: "7%",
        bottom: "25%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: programsArray,
        splitArea: {
          show: true,
        },
        axisLabel: {
          interval: 0,
          rotate: 45,
          width: 100,
          overflow: "truncate",
        },
      },
      yAxis: {
        type: "category",
        data: sortedSeverities,
        splitArea: {
          show: true,
        },
      },
      visualMap: {
        min: 0,
        max: Math.max(...heatmapData.map((item) => item[2])),
        calculable: true,
        orient: "horizontal",
        left: "center",
        bottom: "0%",
        inRange: {
          color: ["#f3e8ff", "#d1b3ff", "#b38dfc", "#7f56d9", "#7f56d9"],
        },
      },
      series: [
        {
          name: "Submissions",
          type: "heatmap",
          data: heatmapData,
          label: {
            show: true,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    };
  };

  const getChartOption = () => {
    switch (chartType) {
      case "bar":
        return getBarChartOption();
      case "line":
        return getLineChartOption();
      case "radar":
        return getRadarChartOption();
      case "pie":
        return getPieChartOption();
      case "donut":
        return getDonutChartOption();
      case "heatmap":
        return getHeatmapChartOption();
      default:
        return getBarChartOption();
    }
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleDownload = (type) => {
    if (chartRef.current) {
      const echartInstance = chartRef.current.getEchartsInstance();
      const base64 = echartInstance.getDataURL({
        type: type === "png" ? "png" : "jpeg",
        pixelRatio: 2,
        backgroundColor: "#fff",
      });

      const link = document.createElement("a");
      link.download = `reward-management-${chartType}-chart.${type}`;
      link.href = base64;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloadDropdownOpen(false);
    }
  };

  const handleExportPDF = async () => {
    if (!chartRef.current) return;

    const chartElement = chartRef.current.getEchartsInstance().getDom();

    try {
      const canvas = await html2canvas(chartElement); // Ensure it's awaited
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [canvas.width, canvas.height],
      });

      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save("chart.pdf");
    } catch (err) {
      console.error("PDF export failed:", err);
      alert("Something went wrong while exporting to PDF.");
    }

    setDownloadDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleDownloadDropdown = () => {
    setDownloadDropdownOpen(!downloadDropdownOpen);
  };

  const selectChartType = (type) => {
    setChartType(type);
    setIsDropdownOpen(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded shadow p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="h5 text-secondary">Loading Chart Data...</h2>
        </div>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "24rem" }}
        >
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded shadow p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="h5 text-danger">Error</h2>
        </div>
        <div
          className="text-danger text-center d-flex align-items-center justify-content-center"
          style={{ height: "24rem" }}
        >
          {error}
          <button onClick={handleRefresh} className="btn btn-primary ms-3">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="bg-white rounded shadow p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="h5 text-secondary">No Data Available</h2>
          <button
            onClick={handleRefresh}
            className="btn btn-link text-primary p-0"
            title="Refresh"
          >
            <RefreshCw size={20} />
          </button>
        </div>
        <div
          className="text-center text-muted d-flex align-items-center justify-content-center"
          style={{ height: "24rem" }}
        >
          No data available for the selected chart type
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded p-4">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 gap-3">
        <div className="dropdown">
          <button
            className="btn dropdown-toggle d-flex align-items-center"
            type="button"
            onClick={toggleDropdown}
          >
            {chartTypes.find((ct) => ct.id === chartType)?.name ||
              "Select Chart Type"}
            <ChevronDown className="ms-2" size={16} />
          </button>
          {isDropdownOpen && (
            <ul className="dropdown-menu show">
              {chartTypes.map((type) => (
                <li key={type.id}>
                  <button
                    className="dropdown-item"
                    onClick={() => selectChartType(type.id)}
                  >
                    {type.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="d-flex gap-2">
          <button onClick={handleRefresh} className="btn" title="Refresh Data">
            <RefreshCw size={20} />
          </button>

          <div className="dropdown">
            <button
              className="btn"
              onClick={toggleDownloadDropdown}
              title="Download Chart"
            >
              <Download size={20} />
            </button>
            {downloadDropdownOpen && (
              <ul className="dropdown-menu dropdown-menu-end show">
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => handleDownload("png")}
                  >
                    Download as PNG
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => handleDownload("jpeg")}
                  >
                    Download as JPEG
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleExportPDF}>
                    Export as PDF
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>

      <div style={{ height: "500px", width: "100%" }}>
        <ReactECharts
          ref={chartRef}
          option={getChartOption()}
          style={{ height: "100%", width: "100%" }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    </div>
  );
};

export default RewardManagementChart;
